// MonitoringCredentials — staff-only card on the client Overview tab for the
// client's credit-monitoring login (MyFreeScoreNow / SmartCredit / IdentityIQ /
// MyScoreIQ / Experian / other). This is the login the credential-based import
// uses, and the one an operator types into a real browser for the providers we
// can't auto-pull (Experian). The username shows as-is; the password is
// write-only from the UI — encrypted server-side and only returned through the
// audited reveal endpoint. Reveal + copy so an operator can grab it without
// retyping.
import { Eye, EyeOff, KeyRound, Loader2, Pencil, ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { clients as clientsApi, MONITORING_PROVIDERS, type Client } from "../lib/api";
import { CopyButton } from "../lib/pii";

function providerLabel(p: string): string {
  return MONITORING_PROVIDERS.find((x) => x.value === p)?.label || p.replace(/_/g, " ");
}

export function MonitoringCredentials({
  client,
  onSaved,
}: {
  client: Client;
  onSaved: (updated: Client) => void;
}) {
  // Only meaningful once a provider is chosen. "not_started" clients have no
  // monitoring login to store yet.
  const provider = client.monitoring;
  const configured = provider && provider !== "not_started";
  const onFile = !!client.has_monitoring_credentials;

  const [editing, setEditing] = useState(false);
  const [username, setUsername] = useState(client.monitoring_username || "");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Revealed creds are tagged with the client id they belong to; `shown` gates
  // rendering so a revealed password can never appear against another client,
  // even for the render between a client switch and the reset effect.
  const [revealed, setRevealed] = useState<{ forId: string; username: string | null; password: string | null } | null>(null);
  const shown = revealed && revealed.forId === client.id ? revealed : null;
  const [revealing, setRevealing] = useState(false);
  const hideTimer = useRef<number | null>(null);
  // Latest rendered client id — used to discard a reveal/save response that
  // lands after the host switched clients (this instance is reused).
  const idRef = useRef(client.id);
  idRef.current = client.id;

  // Reset everything when switching to a different client so a revealed
  // password (or a half-typed edit) can never bleed onto another client's
  // page, and clear any pending auto-hide timer on unmount.
  useEffect(() => {
    setRevealed(null);
    setEditing(false);
    setPassword("");
    setUsername(client.monitoring_username || "");
    setErr(null);
    setRevealing(false);
    setSaving(false);
    if (hideTimer.current) { window.clearTimeout(hideTimer.current); hideTimer.current = null; }
  }, [client.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { if (hideTimer.current) window.clearTimeout(hideTimer.current); }, []);

  if (!configured) return null;

  async function save() {
    setErr(null);
    if (!username.trim()) { setErr("Username is required"); return; }
    if (!onFile && !password.trim()) { setErr("Enter the password"); return; }
    const requestedId = client.id;
    setSaving(true);
    try {
      const r = await clientsApi.saveMonitoring(requestedId, {
        username: username.trim(),
        // Trim only decides whether a password was entered — the value itself
        // keeps its exact characters (a trailing space can be real).
        password: password.trim() ? password : undefined,
      });
      if (idRef.current !== requestedId) return; // switched clients mid-save — don't clobber
      setPassword("");
      setEditing(false);
      setRevealed(null);
      onSaved(r.client);
    } catch (e: any) {
      if (idRef.current !== requestedId) return;
      setErr(e?.message || "Could not save");
    } finally {
      if (idRef.current === requestedId) setSaving(false);
    }
  }

  async function reveal() {
    if (shown) {
      setRevealed(null);
      if (hideTimer.current) { window.clearTimeout(hideTimer.current); hideTimer.current = null; }
      return;
    }
    const requestedId = client.id;
    setRevealing(true);
    setErr(null);
    try {
      const r = await clientsApi.revealMonitoring(requestedId);
      if (idRef.current !== requestedId) return; // switched clients mid-reveal — discard
      setRevealed(r.value ? { forId: requestedId, username: r.value.username, password: r.value.password } : { forId: requestedId, username: null, password: null });
      if (hideTimer.current) window.clearTimeout(hideTimer.current);
      hideTimer.current = window.setTimeout(() => setRevealed(null), 30_000);
    } catch (e: any) {
      if (idRef.current !== requestedId) return;
      setErr(e?.message || "Could not reveal");
    } finally {
      if (idRef.current === requestedId) setRevealing(false);
    }
  }

  const displayUsername = shown?.username ?? client.monitoring_username ?? "—";

  return (
    <div className="panel">
      <div className="panel-header">
        <div>
          <h2><KeyRound size={18} aria-hidden="true" style={{ verticalAlign: "-3px" }} /> {providerLabel(provider)} login</h2>
          <span className="muted small">
            The client's credit-monitoring login, stored encrypted. Used by the report import — and for {providerLabel(provider)} you log in with it directly. Reveal shows the password (the reveal is logged).
          </span>
        </div>
        {onFile && !editing && <span className="pill pill-green"><ShieldCheck size={13} aria-hidden="true" /> On file</span>}
      </div>

      {err && <p className="error-text">{err}</p>}

      {!editing ? (
        <div className="profile-metrics">
          <div>
            <p>Username</p>
            <strong style={{ fontSize: 14, display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span>{displayUsername}</span>
              <CopyButton value={displayUsername !== "—" ? displayUsername : undefined} title="Copy username" />
            </strong>
          </div>
          <div>
            <p>Password</p>
            <strong style={{ fontSize: 14, display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span>{shown ? (shown.password || "—") : (onFile ? "••••••••••" : "—")}</span>
              {onFile && (
                <button
                  type="button"
                  className="revealable-toggle"
                  onClick={reveal}
                  disabled={revealing}
                  title={shown ? "Hide" : "Reveal password (logged)"}
                >
                  {revealing ? <Loader2 size={14} className="spin" /> : shown ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              )}
              {shown?.password && <CopyButton value={shown.password} title="Copy password" />}
            </strong>
          </div>
          <div style={{ alignSelf: "end" }}>
            <button className="ghost-button" onClick={() => { setEditing(true); setUsername(client.monitoring_username || ""); setPassword(""); }}>
              <Pencil size={14} aria-hidden="true" /> {onFile ? "Update" : "Add login"}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="form-row-2">
            <label className="form-field"><span>Username / email *</span>
              <input type="text" autoComplete="off" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="client login" /></label>
            <label className="form-field"><span>{onFile ? "New password (leave blank to keep)" : "Password *"}</span>
              <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" /></label>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button className="primary-button" onClick={save} disabled={saving}>
              {saving ? <Loader2 size={14} className="spin" /> : <ShieldCheck size={14} aria-hidden="true" />} Save login
            </button>
            <button className="ghost-button" onClick={() => { setEditing(false); setPassword(""); setErr(null); }} disabled={saving}>
              Cancel
            </button>
          </div>
        </>
      )}
    </div>
  );
}
